import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import {
  DialogPresets,
  LoaderButtonComponent,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  IAIReceptionistKnowledgeBase,
  IAIReceptionistSettings,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  firstValueFrom,
  initFirestoreModel,
  isPathChanged$,
  WithRef,
} from '@principle-theorem/shared';
import { Observable, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AIReceptionistKnowledgeBaseService } from '../services/ai-receptionist-knowledge-base.service';
import { AIReceptionistSettingsService } from '../services/ai-receptionist-settings.service';
import { AiReceptionistKnowledgeBaseEditorStore } from './ai-receptionist-knowledge-base-editor.store';
import {
  AI_KNOWLEDGE_BASE_SECTION_EDIT_DEBOUNCE_MS,
  AI_KNOWLEDGE_BASE_SECTION_LABELS,
  AI_KNOWLEDGE_BASE_SECTION_ROUTES,
  AiKnowledgeBaseSection,
} from './ai-receptionist-knowledge-base-section';
import { AiReceptionistSettingsEditorStore } from './ai-receptionist-settings-editor.store';
import {
  AiReceptionistImportFromWebsiteDialogComponent,
  type IAiReceptionistImportFromWebsiteDialogData,
  type IAiReceptionistImportFromWebsiteDialogResult,
} from './import-from-website-dialog/ai-receptionist-import-from-website-dialog.component';

@Component({
  selector: 'pr-ai-receptionist-knowledge-base-editor',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatProgressSpinnerModule,
    RouterModule,
    LoaderButtonComponent,
  ],
  templateUrl: './ai-receptionist-knowledge-base-editor.component.html',
  styleUrls: ['./ai-receptionist-knowledge-base-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiReceptionistKnowledgeBaseEditorComponent {
  private readonly _store = inject(AiReceptionistKnowledgeBaseEditorStore);
  private readonly _settingsStore = inject(AiReceptionistSettingsEditorStore);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _dialog = inject(MatDialog);
  private _saveInProgress = false;
  private _importInProgress = false;

  readonly practice = input.required<WithRef<IPractice>>();

  loading$ = this._store.loading$;
  saving$ = this._store.saving$;
  lastSaveError$ = this._store.lastSaveError$;

  sections = Object.values(AiKnowledgeBaseSection);
  sectionLabels = AI_KNOWLEDGE_BASE_SECTION_LABELS;
  sectionRoutes = AI_KNOWLEDGE_BASE_SECTION_ROUTES;

  constructor() {
    const practice$ = toObservable(this.practice);

    practice$.pipe(takeUntilDestroyed()).subscribe((practice) => {
      this._store.setPractice(practice);
      this._settingsStore.setPractice(practice);
    });

    practice$
      .pipe(isPathChanged$('ref.id'), takeUntilDestroyed())
      .subscribe((practice) => {
        void this._loadAiReceptionistKnowledgeBase(practice);
        void this._loadOvieSettings(practice);
      });
  }

  async saveAndPush(): Promise<void> {
    if (this._saveInProgress || this._store.getState().saving) {
      return;
    }
    this._saveInProgress = true;

    try {
      await this._waitForSectionEdits();
      const practice = this.practice();

      this._store.saveAndPush({ practice });
      await this._saveSettled(this._store.saving$);
      this._settingsStore.saveSettingsAndPush({ practice });
      await this._saveSettled(this._settingsStore.saving$);

      this._showSaveResult();
    } finally {
      this._saveInProgress = false;
    }
  }

  /**
   * Waits for a pending section edit to reach the store first: the review
   * updates custom sections by position, so it must see the Knowledge Base
   * the accepted values are applied to. The dialog is modal, so nothing
   * changes it while the review is open.
   */
  async importFromWebsite(): Promise<void> {
    if (this._importInProgress) {
      return;
    }
    this._importInProgress = true;
    try {
      await this._importFromWebsite();
    } finally {
      this._importInProgress = false;
    }
  }

  private async _importFromWebsite(): Promise<void> {
    await this._waitForSectionEdits();
    const knowledgeBase = this._store.getState().aiKnowledgeBase;
    if (!knowledgeBase) {
      return;
    }
    const data: IAiReceptionistImportFromWebsiteDialogData = {
      practice: this.practice(),
      knowledgeBase,
    };
    const result = await firstValueFrom(
      this._dialog
        .open<
          AiReceptionistImportFromWebsiteDialogComponent,
          IAiReceptionistImportFromWebsiteDialogData,
          IAiReceptionistImportFromWebsiteDialogResult | undefined
        >(
          AiReceptionistImportFromWebsiteDialogComponent,
          DialogPresets.large({
            data,
            autoFocus: '#import-website-url',
            restoreFocus: true,
          })
        )
        .afterClosed()
    );
    if (!result) {
      return;
    }
    this._store.applyImportProposal(result);
  }

  private async _waitForSectionEdits(): Promise<void> {
    await firstValueFrom(timer(AI_KNOWLEDGE_BASE_SECTION_EDIT_DEBOUNCE_MS));
  }

  private async _saveSettled(saving$: Observable<boolean>): Promise<void> {
    await firstValueFrom(saving$.pipe(filter((saving) => !saving)));
  }

  private _showSaveResult(): void {
    const error =
      this._store.getState().lastSaveError ??
      this._settingsStore.getState().lastSaveError;

    if (error) {
      this._snackBar.open(error, 'Dismiss', { duration: 10000 });
      return;
    }

    this._snackBar.open('AI Settings saved successfully', 'OK', {
      duration: 5000,
    });
  }

  private async _loadAiReceptionistKnowledgeBase(
    practice: WithRef<IPractice>
  ): Promise<void> {
    try {
      const existing =
        await AIReceptionistKnowledgeBaseService.getKnowledgeBase(practice);
      this._store.setAiKnowledgeBase(existing ?? this._getDefaultAiSettings());
    } catch {
      this._store.setAiKnowledgeBase(this._getDefaultAiSettings());
    }
  }

  private async _loadOvieSettings(practice: WithRef<IPractice>): Promise<void> {
    try {
      const existing =
        await AIReceptionistSettingsService.getSettings(practice);
      this._settingsStore.setSettings(
        existing ?? this._getDefaultOvieSettings()
      );
    } catch {
      this._settingsStore.setSettings(this._getDefaultOvieSettings());
    }
  }

  private _getDefaultOvieSettings(): IAIReceptionistSettings {
    return {
      ...initFirestoreModel(),
    };
  }

  private _getDefaultAiSettings(): IAIReceptionistKnowledgeBase {
    return {
      ...initFirestoreModel(),
      customSections: [],
    };
  }
}
